import toast from "react-hot-toast";

const formatValue = (value, unit) => {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value ?? "");
    const formatted = Number.isInteger(n) ? String(n) : String(n);
    return unit === "days" ? `${formatted} j` : `${formatted} h`;
};

/**
 * Extrait un message texte depuis une réponse API Laravel (string, tableau de status, violations).
 */
export const extractApiErrorMessage = (data, fallback = "Une erreur est survenue.") => {
    if (!data) return fallback;

    const msg = data.message;
    if (typeof msg === "string" && msg.trim() !== "") {
        return msg;
    }

    if (Array.isArray(msg)) {
        const lines = msg
            .map((item) => {
                if (typeof item === "string") return item;
                if (item && typeof item.message === "string") return item.message;
                return null;
            })
            .filter(Boolean);
        if (lines.length > 0) {
            return lines.join("\n");
        }
    }

    const violations = data.violations ?? [];
    if (Array.isArray(violations) && violations.length > 0) {
        return violations.map(formatPlanningLimitMessage).join("\n");
    }

    return fallback;
};

export const formatPlanningLimitMessage = (item) => {
    const name = item.rule_name ?? item.ruleName ?? "Règle";
    const unit = item.limit_unit ?? item.limitUnit ?? "hours";
    const actual = item.actual_value ?? item.actualValue ?? item.actual_hours ?? item.actualHours;
    const max = item.max_value ?? item.maxValue ?? item.max_hours ?? item.maxHours;
    const period = item.period_label ?? item.periodLabel ?? "";
    return `${name} : ${formatValue(actual, unit)} / ${formatValue(max, unit)} max${period ? ` (${period})` : ""}`;
};

export const notifyPlanningLimitWarnings = (warnings = []) => {
    warnings.forEach((w) => {
        toast(formatPlanningLimitMessage(w), {
            duration: 6000,
            icon: "⚠️",
            style: {
                background: "#fffbeb",
                color: "#92400e",
            },
        });
    });
};

export const notifyPlanningLimitViolations = (violations = []) => {
    violations.forEach((v) => {
        toast.error(formatPlanningLimitMessage(v), { duration: 8000 });
    });
};

/**
 * @param {object} responseData - axios response.data
 */
export const handlePlanningLimitSuccess = (responseData) => {
    notifyPlanningLimitWarnings(responseData?.warnings ?? []);
};

/**
 * Après génération de planning en masse : affiche alertes et dépassements sans bloquer.
 * @param {object} responseData - axios response.data
 */
export const handlePlanningCreationSuccess = (responseData) => {
    const issues = [
        ...(responseData?.warnings ?? []),
        ...(responseData?.violations ?? []),
    ];
    notifyPlanningLimitWarnings(issues);
};

/**
 * @param {Error} error
 * @param {boolean} canForce
 * @returns {Promise<boolean>} true if caller should retry with force
 */
export const promptForcePlanningLimit = async (error, canForce) => {
    const violations = error?.response?.data?.violations ?? [];
    if (!violations.length) {
        return false;
    }

    notifyPlanningLimitViolations(violations);

    if (!canForce) {
        return false;
    }

    const lines = violations.map(formatPlanningLimitMessage).join("\n");
    return window.confirm(
        `Dépassement des limites détecté :\n\n${lines}\n\nForcer l'enregistrement ?`
    );
};

/**
 * Exécute une opération planning ; en cas de blocage, propose le forçage si autorisé.
 */
export const runPlanningOpWithForce = async (operation, canForce) => {
    try {
        const resp = await operation(false);
        handlePlanningLimitSuccess(resp?.data ?? {});
        return resp;
    } catch (error) {
        const shouldForce = await promptForcePlanningLimit(error, canForce);
        if (!shouldForce) {
            throw error;
        }
        const resp = await operation(true);
        handlePlanningLimitSuccess(resp?.data ?? {});
        toast.success("Enregistrement forcé.");
        return resp;
    }
};
